import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
} from '@material-ui/core'

import {
    CircularProgress,
    useTheme,
    TextField,
    Modal,
    Slide,
    Paper,
    Tabs,
    Tab,
    Tooltip,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'

import { Container, SideDrawerContainer } from '../../components'
import {
    useAppDispatch,
    useAptConfig,
    useRootInfrastructure,
    useUser,
} from '../../hooks'
import {
    GET_AREA_CONFIG_LIST_REQUEST,
    GET_UNIT_CONFIG_LIST_REQUEST,
    RootState,
    setUnitConfigList,
    setWorkSpaceUser,
} from '../../store'
import { useInfrastructureConfigStyles } from './useInfrastructureConfigStyles'
import { AreaConfigRow } from './AreaConfigRow'
import { UnitConfigRow } from './UnitConfigRow'
import {
    getFlatInfrastructure,
    IdentifiableNamedObject,
    InventoryConfig,
    AreaConfig,
    Unit,
    UnitConfig,
    Company,
} from '../../models'
import { InvConfigStatusCostDrawer } from '../InventoryConfig'
import { CreateAreaConfigForm } from './CreateAreaConfigForm'
import { CreateInfrastructureDrawer } from './CreateInfrastructureDrawer'
import { InvAreaConfigDrawer } from './InvAreaConfigDrawer'
import { IssueOptionsDrawer } from './IssueOptionsDrawer'
import { axiosInstance, isAccountManager } from '../../helpers'
import { InventoryGroupTabPanel } from './InventoryGroupTabPanel'
import { UnitAreaConfigMapForm } from './UnitAreaConfigMapForm'
import { CommonAreaRule } from '../../models/Config/types'
import { UnitVisibilityManager } from './UnitVisibilityManager'

export const FORM_CLOSED = 0
export const INVENTORY_CONFIG = 1
export const INVENTORY_GROUP = 2
export const AREA_CONFIG = 3
export const UNIT_CONFIG = 4
export const ISSUE_CONFIG = 5
export const UNIT_TAB = 6

type ConfigSelection =
    | typeof INVENTORY_CONFIG
    | typeof INVENTORY_GROUP
    | typeof AREA_CONFIG
    | typeof UNIT_CONFIG
    | typeof ISSUE_CONFIG
    | typeof UNIT_TAB
type FormOpenState = typeof FORM_CLOSED | ConfigSelection

export interface DrawerState {
    formOpen: FormOpenState
    selected?: IdentifiableNamedObject
}

const closedDrawer: DrawerState = {
    formOpen: FORM_CLOSED,
}

export const InfrastructureConfig = () => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const history = useHistory()
    const location = useLocation()
    const dispatchRedux = useDispatch()

    const styles = useInfrastructureConfigStyles()

    const { workspaceUser } = useUser()

    const areaConfigLoading = useSelector(
        (state: RootState) =>
            state.aptConfig.isLoading[GET_AREA_CONFIG_LIST_REQUEST],
    )
    const unitConfigLoading = useSelector(
        (state: RootState) =>
            state.aptConfig.isLoading[GET_UNIT_CONFIG_LIST_REQUEST],
    )

    const {
        inventoryConfigList,
        areaConfigList,
        unitConfigList,
        getUnitConfigMap,
        damageConfigList,
    } = useAptConfig({
        inventoryConfigList: true,
        areaConfigList: true,
        unitConfigList: true,
        damageConfigList: true,
        cleanUp: true,
    })

    const {
        tree,
        updateUnitVisibility,
        bulkUpdateUnitVisibility,
    } = useRootInfrastructure(true)

    const unitConfigCountMap = useMemo(() => {
        const countMap: { [id: number]: number } = {}
        getFlatInfrastructure(tree.root).forEach((item) => {
            const unit = item as Unit
            if (unit.unit_config !== undefined) {
                countMap[unit.unit_config] =
                    (countMap[unit.unit_config] ?? 0) + 1
            }
        })
        return countMap
    }, [tree])

    const [searchValue, setSearchValue] = useState('')

    const [modalOpen, setModalOpen] = useState<FormOpenState>(FORM_CLOSED)
    const [commonAreaRuleModalOpen, setCommonAreaRuleModalOpen] = useState(
        false,
    )
    const [
        selectedCommonAreaRule,
        setSelectedCommonAreaRule,
    ] = useState<string>(
        workspaceUser?.active_workspace.common_area_rule_type ||
            CommonAreaRule.NO_RULE,
    )
    const TabHashMap: { [key: number]: string } = {
        [INVENTORY_GROUP]: 'inventory-groups',
        [AREA_CONFIG]: 'area-types',
        [UNIT_CONFIG]: 'unit-types',
        [UNIT_TAB]: 'units',
    }

    const hashToTab = Object.fromEntries(
        Object.entries(TabHashMap).map(([key, value]) => [
            value,
            parseInt(key),
        ]),
    )

    console.log(hashToTab)

    const [tab, setTab] = useState<ConfigSelection>(() => {
        const hash = location.hash.replace('#', '')
        return (hash in hashToTab
            ? hashToTab[hash]
            : INVENTORY_GROUP) as ConfigSelection
    })

    useEffect(() => {
        const hash = location.hash.replace('#', '')
        if (hash in hashToTab) {
            setTab(hashToTab[hash] as ConfigSelection)
        }
    }, [location])

    const [openCostDrawer, setOpenCostDrawer] = useState<
        InventoryConfig | undefined
    >(undefined)
    const [
        openInfrastructureDrawer,
        setOpenInfrastructureDrawer,
    ] = useState<DrawerState>(closedDrawer)

    const [drawerOpen, setDrawerOpen] = useState(closedDrawer)

    const unitConfigMap = getUnitConfigMap()

    const handleChangeTab = (newTab: ConfigSelection) => {
        if (newTab !== tab) {
            setOpenCostDrawer(undefined)
            setOpenInfrastructureDrawer(closedDrawer)
            setDrawerOpen(closedDrawer)
            setModalOpen(FORM_CLOSED)
            setTab(newTab)
            history.push(`/infrastructure-configure#${TabHashMap[newTab]}`)
        }
    }

    const handeInfrastructureDrawerChange = (drawerState: DrawerState) => {
        setOpenCostDrawer(undefined)
        setDrawerOpen(closedDrawer)
        setModalOpen(FORM_CLOSED)
        setOpenInfrastructureDrawer(drawerState)
    }

    const handleDrawerChange = (drawer?: DrawerState) => {
        setOpenCostDrawer(undefined)
        setOpenInfrastructureDrawer(closedDrawer)
        setModalOpen(FORM_CLOSED)
        if (drawer === undefined) {
            setDrawerOpen(closedDrawer)
        } else {
            setDrawerOpen(drawer)
        }
    }

    const handleCostDrawerChange = (
        selectedInvConfig: InventoryConfig | undefined,
    ) => {
        setDrawerOpen(closedDrawer)
        setOpenInfrastructureDrawer(closedDrawer)
        setModalOpen(FORM_CLOSED)
        setOpenCostDrawer(selectedInvConfig)
    }

    const handleSaveCommonAreaRule = () => {
        axiosInstance
            .post('company/apartment/set_common_area_rule_type/', {
                common_area_rule: selectedCommonAreaRule,
            })
            .then((res) => {
                const apartment: Company = res.data
                toast.success(
                    `Default common area rule set to: ${selectedCommonAreaRule}`,
                )
                setCommonAreaRuleModalOpen(false)
                if (workspaceUser?.active_workspace) {
                    workspaceUser.active_workspace = apartment
                    dispatch(setWorkSpaceUser(workspaceUser))
                }
            })
            .catch((err) => {
                toast.error(
                    `Error setting default common area rule: ${err.response.data.message}`,
                )
            })
    }

    return (
        <SideDrawerContainer
            open={
                drawerOpen.formOpen !== FORM_CLOSED ||
                openCostDrawer !== undefined
            }
        >
            <Container style={{ flex: 1, height: 'calc(100vh - 104px)' }}>
                <Container
                    style={{
                        ...styles.columnStyle,
                        marginRight: theme.spacing(1),
                    }}
                >
                    <Tabs
                        style={{ backgroundColor: theme.palette.grey[200] }}
                        value={tab}
                        onChange={(_, v) => {
                            handleChangeTab(v)
                        }}
                    >
                        <Tab label="Inventory Groups" value={INVENTORY_GROUP} />
                        <Tab label="Area types" value={AREA_CONFIG} />
                        <Tab label="Unit types" value={UNIT_CONFIG} />
                        <Tab label="Units" value={UNIT_TAB} />
                    </Tabs>

                    <Container style={{ alignItems: 'center', height: 50 }}>
                        {tab !== UNIT_TAB && (
                            <TextField
                                value={searchValue}
                                placeholder="Search"
                                onChange={(e) => setSearchValue(e.target.value)}
                                style={{
                                    width: 300,
                                    marginLeft: theme.spacing(1),
                                }}
                            />
                        )}

                        <div style={{ flex: 1 }} />
                        {tab === AREA_CONFIG && (
                            <>
                                <Tooltip title="Areas must be assigned to a unit for them to appear on your inspection">
                                    <InfoIcon fontSize="small" color="action" />
                                </Tooltip>
                                <Button
                                    variant="contained"
                                    style={{
                                        margin: theme.spacing(3, 0, 3, 2),
                                        backgroundColor: '#008C85',
                                        color: 'white',
                                        textTransform: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                        setCommonAreaRuleModalOpen(true)
                                    }
                                >
                                    Set Default Common Area Rule
                                </Button>
                                {isAccountManager(workspaceUser) && (
                                    <Button
                                        variant="contained"
                                        style={{
                                            margin: theme.spacing(3, 0, 3, 2),
                                            backgroundColor: '#008C85',
                                            color: 'white',
                                            textTransform: 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setModalOpen(tab)}
                                    >
                                        {`Create New Area Type`}
                                    </Button>
                                )}
                            </>
                        )}

                        {tab === INVENTORY_CONFIG && (
                            <Button
                                variant="contained"
                                style={{
                                    margin: theme.spacing(3, 0, 3, 2),
                                    backgroundColor: '#008C85',
                                    color: 'white',
                                    textTransform: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setModalOpen(tab)}
                            >
                                {`+ Add New Item`}
                            </Button>
                        )}

                        {tab === UNIT_CONFIG &&
                            isAccountManager(workspaceUser) && (
                                <>
                                    <Button
                                        variant="contained"
                                        style={{
                                            margin: theme.spacing(3, 0, 3, 2),
                                            backgroundColor: '#008C85',
                                            color: 'white',
                                            textTransform: 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            axiosInstance
                                                .post(
                                                    'apt_config/unit_config/bulk_set_shows_on_schedule/',
                                                    {
                                                        shows_on_schedule: true,
                                                    },
                                                )
                                                .then((res) => {
                                                    dispatch(
                                                        setUnitConfigList(
                                                            res.data,
                                                        ),
                                                    )
                                                    toast.success(
                                                        'All show on schedule',
                                                    )
                                                })
                                        }}
                                    >
                                        Set All Shows on Schedule
                                    </Button>
                                    <Button
                                        variant="contained"
                                        style={{
                                            margin: theme.spacing(3, 0, 3, 2),
                                            backgroundColor: '#008C85',
                                            color: 'white',
                                            textTransform: 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            axiosInstance
                                                .post(
                                                    'apt_config/unit_config/bulk_set_shows_on_schedule/',
                                                    {
                                                        shows_on_schedule: false,
                                                    },
                                                )
                                                .then((res) => {
                                                    dispatch(
                                                        setUnitConfigList(
                                                            res.data,
                                                        ),
                                                    )
                                                    toast.success(
                                                        'None show on schedule',
                                                    )
                                                })
                                        }}
                                    >
                                        Set None Shows on Schedule
                                    </Button>
                                    <Button
                                        variant="contained"
                                        style={{
                                            margin: theme.spacing(3, 0, 3, 2),
                                            backgroundColor: '#008C85',
                                            color: 'white',
                                            textTransform: 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setModalOpen(tab)}
                                    >
                                        Unit Config Mapping
                                    </Button>
                                </>
                            )}
                    </Container>

                    <div style={{ display: 'flex', flex: 1 }}>
                        {tab === INVENTORY_GROUP && (
                            <InventoryGroupTabPanel
                                inventoryConfigs={inventoryConfigList}
                                areaConfigs={areaConfigList}
                            />
                        )}

                        {tab === AREA_CONFIG && (
                            <Panel<AreaConfig>
                                isLoading={areaConfigLoading}
                                data={areaConfigList?.filter((areaConf) =>
                                    areaConf.name
                                        .toLocaleLowerCase()
                                        .includes(
                                            searchValue.toLocaleLowerCase(),
                                        ),
                                )}
                                renderItem={(item) => (
                                    <AreaConfigRow
                                        key={`AREA_CONFIG_ROW_${item.id}`}
                                        areaConfig={item}
                                        selected={
                                            drawerOpen.formOpen ===
                                                AREA_CONFIG &&
                                            drawerOpen.selected?.id === item.id
                                        }
                                        onClick={(id) => {
                                            if (
                                                id ===
                                                    drawerOpen.selected?.id &&
                                                drawerOpen.formOpen ===
                                                    AREA_CONFIG
                                            ) {
                                                handleDrawerChange(closedDrawer)
                                            } else {
                                                handleDrawerChange({
                                                    formOpen: AREA_CONFIG,
                                                    selected: item,
                                                })
                                            }
                                        }}
                                        onClickPlus={(areaConfig) => {
                                            if (
                                                areaConfig.id ===
                                                    openInfrastructureDrawer
                                                        .selected?.id &&
                                                openInfrastructureDrawer.formOpen ===
                                                    AREA_CONFIG
                                            ) {
                                                handeInfrastructureDrawerChange(
                                                    closedDrawer,
                                                )
                                            } else {
                                                handeInfrastructureDrawerChange(
                                                    {
                                                        formOpen: AREA_CONFIG,
                                                        selected: item,
                                                    },
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        )}

                        {tab === UNIT_CONFIG && (
                            <Panel<UnitConfig>
                                isLoading={unitConfigLoading}
                                data={unitConfigList?.filter((unitConf) =>
                                    unitConf.name
                                        .toLocaleLowerCase()
                                        .includes(
                                            searchValue.toLocaleLowerCase(),
                                        ),
                                )}
                                renderItem={(item) => (
                                    <UnitConfigRow
                                        key={`UNIT_CONFIG_ROW_${item.id}`}
                                        unitConfig={item}
                                        unitCount={
                                            unitConfigCountMap[item.id] ?? 0
                                        }
                                        onClickPlus={(unitConfig) => {
                                            if (
                                                unitConfig.id ===
                                                    openInfrastructureDrawer
                                                        .selected?.id &&
                                                openInfrastructureDrawer.formOpen ===
                                                    UNIT_CONFIG
                                            ) {
                                                handeInfrastructureDrawerChange(
                                                    closedDrawer,
                                                )
                                            } else {
                                                handeInfrastructureDrawerChange(
                                                    {
                                                        formOpen: UNIT_CONFIG,
                                                        selected: item,
                                                    },
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        )}

                        {tab === UNIT_TAB && (
                            <UnitVisibilityManager
                                root={tree.root}
                                updateUnitVisibility={updateUnitVisibility}
                                bulkUpdateUnitVisibility={
                                    bulkUpdateUnitVisibility
                                }
                            />
                        )}
                    </div>
                </Container>

                <Modal
                    open={modalOpen !== FORM_CLOSED}
                    onClose={() => setModalOpen(FORM_CLOSED)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Slide direction="up" in={modalOpen !== FORM_CLOSED}>
                        <Paper>
                            {modalOpen === AREA_CONFIG && (
                                <CreateAreaConfigForm
                                    onClose={() => setModalOpen(FORM_CLOSED)}
                                />
                            )}

                            {modalOpen === UNIT_CONFIG && (
                                <UnitAreaConfigMapForm
                                    unitConfigList={unitConfigList}
                                    areaConfigList={areaConfigList}
                                />
                            )}
                        </Paper>
                    </Slide>
                </Modal>
            </Container>

            <CreateInfrastructureDrawer
                open={openInfrastructureDrawer}
                areaConfigList={areaConfigList}
                unitConfigList={unitConfigList}
                unitConfigMap={unitConfigMap}
                handleDrawerChange={handleDrawerChange}
            />

            <InvAreaConfigDrawer
                handleDrawerChange={handleDrawerChange}
                areaConfigList={areaConfigList}
                inventoryConfigList={inventoryConfigList ?? []}
                open={drawerOpen}
            />

            <InvConfigStatusCostDrawer
                open={openCostDrawer !== undefined}
                onClose={() => handleCostDrawerChange(undefined)}
                invConf={openCostDrawer}
            />

            <IssueOptionsDrawer
                open={drawerOpen}
                handleDrawerChange={handleDrawerChange}
                damageConfigList={damageConfigList ?? []}
            />

            {/* Common Area Rule Modal */}
            <Dialog
                open={commonAreaRuleModalOpen}
                onClose={() => setCommonAreaRuleModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Set Default Common Area Rule</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select a rule that determines how common area status is
                        calculated based on bedroom statuses.
                    </DialogContentText>
                    <FormControl
                        component="fieldset"
                        style={{ marginTop: theme.spacing(2) }}
                    >
                        <RadioGroup
                            value={selectedCommonAreaRule}
                            onChange={(e) =>
                                setSelectedCommonAreaRule(e.target.value)
                            }
                        >
                            <FormControlLabel
                                value={CommonAreaRule.NO_RULE}
                                control={<Radio />}
                                label="No Rule - Common area status is set independently"
                            />
                            <FormControlLabel
                                value={CommonAreaRule.ANY_VACANT}
                                control={<Radio />}
                                label="Any Vacant - If any bedroom is Vacant, TI, or TO, common area is Vacant"
                            />
                            <FormControlLabel
                                value={CommonAreaRule.ANY_RENEWAL}
                                control={<Radio />}
                                label="Any Renewal - If any bedroom is Renewal, common area is Renewal"
                            />
                            <FormControlLabel
                                value={CommonAreaRule.MAJORITY_VACANT}
                                control={<Radio />}
                                label="Majority Vacant - If 50% or more bedrooms are Vacant, TI, or TO, common area is Vacant"
                            />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCommonAreaRuleModalOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveCommonAreaRule}
                        color="primary"
                        variant="contained"
                        disabled={!selectedCommonAreaRule}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </SideDrawerContainer>
    )
}

interface PanelProps<T> {
    isLoading: boolean
    data?: T[]
    renderItem: (item: T) => JSX.Element | null
}

function Panel<T>(props: PanelProps<T>) {
    const styles = useInfrastructureConfigStyles()

    const content = props.isLoading ? (
        <Container
            style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
            scrollY
        >
            <CircularProgress
                style={{ alignSelf: 'center', justifySelf: 'center' }}
                size={100}
            />
        </Container>
    ) : (
        <Container style={{ ...styles.columnStyle }}>
            {props.data?.map(props.renderItem)}
        </Container>
    )

    return (
        <Container
            style={{ ...styles.columnStyle, ...styles.panelBorder }}
            scrollY
        >
            {content}
        </Container>
    )
}
